import { getCampaignList } from '@/api/domain/activity'
import { CAMPAIGN_STATUS_OPTIONS, CAMPAIGN_STATUS } from './leaseCampaignConst'
import moment from 'moment'
import { operateMaxShowLength } from '@/store/dict/config'

// 活动状态 → 展示名称映射
export const STATUS_MAP = {
  [CAMPAIGN_STATUS.NOT_STARTED]: '未开始',
  [CAMPAIGN_STATUS.IN_PROGRESS]: '进行中',
  [CAMPAIGN_STATUS.EXPIRED]: '已过期',
  [CAMPAIGN_STATUS.DISABLED]: '已失效',
}

// 活动状态 → tag 颜色映射
export const STATUS_TAG_MAP = {
  [CAMPAIGN_STATUS.NOT_STARTED]: { type: 'info' },
  [CAMPAIGN_STATUS.IN_PROGRESS]: { type: 'success' },
  [CAMPAIGN_STATUS.EXPIRED]: { type: 'warning' },
  [CAMPAIGN_STATUS.DISABLED]: { type: 'danger' },
}

/**
 * 将后端响应 item 映射为前端展示字段
 */
export function mapItem(item) {
  return {
    ...item,
    campaignName: item.activityName,
    status: item.activityState,
    statusName: STATUS_MAP[item.activityState] || '--',
    fullRentGiftDays: item.presentDays,
    bindPlanCount: item.planCount,
    bindPlanIds: item.planIdList,
  }
}

/**
 * 包装 API 函数，在数据返回前做字段映射
 */
export async function fetchList(params = {}) {
  const response = await getCampaignList(params)
  const { code, data = {} } = response || {}
  if (code !== 1) return response

  return {
    ...response,
    data: {
      ...data,
      items: (data.items || []).map(mapItem),
    },
  }
}

/**
 * 生成 BaseListManagement 需要的 configData
 * @param {Object} deps
 * @param {Function} deps.judgePermission - 权限校验函数
 * @param {Function} deps.doView - 查看
 * @param {Function} deps.doEdit - 编辑
 * @param {Function} deps.doOffline - 下线
 * @param {Function} deps.doDelete - 删除
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const configDataFn = ({
  judgePermission = () => false,
  /* eslint-disable @typescript-eslint/no-empty-function */
  doView = noop,
  doEdit = noop,
  doOffline = noop,
  doDelete = noop,
  /* eslint-enable @typescript-eslint/no-empty-function */
}) => {
  const configData = {
    formData: {
      list: [
        {
          el: 'input',
          itemConfig: {
            label: '活动名称',
            prop: 'campaignName',
          },
          elConfig: {
            clearable: true,
            placeholder: '请输入活动名称',
          },
        },
        {
          el: 'select',
          itemConfig: {
            label: '活动状态',
            prop: 'status',
          },
          elConfig: {
            clearable: true,
            options: {
              label: 'label',
              value: 'value',
              list: [
                { label: '全部', value: '' },
                ...CAMPAIGN_STATUS_OPTIONS,
              ],
            },
          },
        },
        {
          el: 'date-picker',
          itemConfig: {
            label: '活动时间',
            prop: 'dateRange',
          },
          elConfig: {
            type: 'daterange',
            clearable: true,
            'value-format': 'yyyy-MM-dd',
            'start-placeholder': '开始日期',
            'end-placeholder': '结束日期',
          },
        },
      ],
      dealWithSearch: (formData) => {
        const [startDate = '', endDate = ''] = formData.dateRange || []
        return {
          activityName: formData.campaignName || undefined,
          activityState:
            formData.status !== '' && formData.status !== undefined
              ? formData.status
              : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }
      },
    },
    isShowDepartment: false,
    tableData: {
      columns: [
        {
          name: 'campaignName',
          columnProps: {
            prop: 'campaignName',
            label: '活动名称',
            'min-width': 200,
          },
        },
        {
          name: 'dateRange',
          columnProps: {
            prop: 'dateRange',
            label: '活动进行时间',
            'min-width': 220,
          },
          getText: ({ row }) => {
            const start = row.startDate || '--'
            const end = row.endDate || '--'
            return `${start} 至 ${end}`
          },
        },
        {
          name: 'preFreeRentDays',
          columnProps: {
            prop: 'preFreeRentDays',
            label: '前置免租',
            'min-width': 100,
          },
          getText: ({ row }) => `${row.preFreeRentDays ?? 0}天`,
        },
        {
          name: 'fullRentGiftDays',
          columnProps: {
            prop: 'fullRentGiftDays',
            label: '满租赠送',
            'min-width': 100,
          },
          getText: ({ row }) => `${row.fullRentGiftDays ?? 0}天`,
        },
        {
          name: 'bindPlanCount',
          columnProps: {
            prop: 'bindPlanCount',
            label: '关联方案',
            'min-width': 100,
          },
          getText: ({ row }) => `${row.bindPlanCount ?? 0}个方案`,
        },
        {
          name: 'statusName',
          type: 'tag',
          columnProps: {
            prop: 'statusName',
            label: '状态',
            'min-width': 100,
          },
          getTagText: ({ row }) => row.statusName,
          tagProps: ({ row }) => STATUS_TAG_MAP[row.status] || { type: 'info' },
        },
        {
          name: 'createTime',
          columnProps: {
            prop: 'createTime',
            label: '创建时间',
            'min-width': 180,
          },
          getText: ({ row }) => {
            if (!row.gmtCreate) return '--'
            return moment(row.gmtCreate).format('YYYY-MM-DD HH:mm')
          },
        },
        {
          name: 'operate',
          type: 'operate',
          columnProps: {
            label: '操作',
            fixed: 'right',
            width: 200,
          },
          maxShowLength: operateMaxShowLength,
          getOperateList: ({ row }) => {
            const btns = []
            const { status } = row

            if (judgePermission('lease_campaign_detail')) {
              btns.push({
                name: 'view',
                label: '查看',
                action: () => doView(row),
              })
            }

            if (
              [CAMPAIGN_STATUS.NOT_STARTED, CAMPAIGN_STATUS.IN_PROGRESS].includes(status) &&
              judgePermission('lease_campaign_edit')
            ) {
              btns.push({
                name: 'edit',
                label: '编辑',
                action: () => doEdit(row),
              })
            }

            if (
              [CAMPAIGN_STATUS.NOT_STARTED, CAMPAIGN_STATUS.IN_PROGRESS].includes(status) &&
              judgePermission('lease_campaign_offline')
            ) {
              btns.push({
                name: 'offline',
                label: '下线',
                action: () => doOffline(row),
              })
            }

            if (judgePermission('lease_campaign_delete')) {
              btns.push({
                name: 'delete',
                label: '删除',
                action: () => doDelete(row),
              })
            }

            return btns
          },
        },
      ],
      apiFn: fetchList,
    },
  }

  return configData
}
